// react
import React, { useRef } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';

const SCROLL_AMOUNT = 285;

/**
 * Carousel displays a series of items in a horizontally scrollable track.
 */
export const Carousel = ({ items, title, isLoading }: CarouselProps) => {
  const trackRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'prev' | 'next') => {
    if (!trackRef.current) return;
    const amount = direction === 'next' ? SCROLL_AMOUNT : -SCROLL_AMOUNT;
    trackRef.current.scrollBy({ left: amount, behavior: 'smooth' });
  };

  if (!items || items.length === 0) return null;

  return (
    <StyledCarousel $isLoading={isLoading}>
      <CarouselHeader>
        <Left>
          <LoadingTransition
            isLoading={isLoading}
            loading={
              <Skeleton
                shape='title'
                width='400px'
                height='36px'
                isMargin={false}
              />
            }
            content={<Title>{title}</Title>}
          />
        </Left>
        <Right>
          <StyledButton onClick={() => scroll('prev')}>
            <Icon icon='chevron-left' fontSize={16} />
          </StyledButton>
          <StyledButton onClick={() => scroll('next')}>
            <Icon icon='chevron-right' fontSize={16} />
          </StyledButton>
        </Right>
      </CarouselHeader>
      <ScrollTrack ref={trackRef}>
        {items.map((item) => (
          <StyledCarouselItem key={item.id}>
            {item.renderItem}
          </StyledCarouselItem>
        ))}
      </ScrollTrack>
    </StyledCarousel>
  );
};

interface StyledCarouselProps {
  $isLoading?: boolean;
}
const StyledCarousel = styled.div<StyledCarouselProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  align-items: stretch;
  gap: ${(props) => props.theme.spacing[12]};

  ${(props) =>
    props.$isLoading &&
    css`
      pointer-events: none;
    `}
`;

const CarouselHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.body.xl.medium};
`;

const Left = styled.div``;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
`;

const ScrollTrack = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  margin: -${(props) => props.theme.spacing[8]};

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
`;

const StyledCarouselItem = styled.div`
  flex-shrink: 0;
  scroll-snap-align: start;
  padding: ${(props) => props.theme.spacing[8]};
  padding-top: ${(props) => props.theme.spacing[12]};
  padding-bottom: ${(props) => props.theme.spacing[12]};
  width: 266px;
  box-sizing: border-box;

  & > * {
    max-width: 100%;
  }
`;

const StyledButton = styled.button`
  all: unset;
  color: ${(props) => props.theme.color.neutral[80]};
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  max-width: 32px;
  height: 32px;
  max-height: 32px;
  border-radius: 99999px;
  border: 1px solid ${(props) => props.theme.color.neutral[80]};

  transition-duration: 0.2s;
  &:hover {
    transform: scale(1.1);
    color: ${(props) => props.theme.color.neutral[90]};
  }
`;

interface CarouselItem {
  id: string;
  renderItem: React.ReactNode;
}

interface CarouselProps {
  items: CarouselItem[];
  title?: string;
  isLoading?: boolean;
}

Carousel.displayName = 'Carousel';

export default Carousel;
